import { HttpErrorResponse }  from '@angular/common/http';
import { Injectable }         from '@angular/core';
import { ENTITY_TYPE }        from '@app/core/interfaces/entity.interface';
import { APP_CONFIG }         from '@appConfig/_app-config_master';
import { APP_MODE }           from '@appConfig/app-mode-config';
import {
	RequestCompleteBaseState,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                             from '@core/interfaces/api.interface';
import { TranslationService } from '@core/services/translation.service';
import { LoggerService }      from '@shared/services/logger.service';
import { ToastrService }      from 'ngx-toastr';


export interface IToasterConfig {
	closeButton: boolean;
	enableHtml: boolean;
	messageClass: string;
	progressBar: boolean;
	tapToDismiss: boolean;
	timeOut: number;
	titleClass: string;
}

@Injectable( { providedIn: 'root' } )
export class ToasterService {
	private toasterConfig: IToasterConfig = {
		closeButton: false,
		enableHtml: false,
		messageClass: 'text-uppercase',
		progressBar: true,
		tapToDismiss: true,
		timeOut: 7000,
		titleClass: 'h4'
	};

	constructor(
		private readonly _loggerService: LoggerService,
		private readonly _translationService: TranslationService,
		public toaster: ToastrService
	) {}

	private showDeveloperMissingTranslationWarning( warningMessage: string, i18nStr: string ): void {
		// informs developer about missing translation for error code
		if ( APP_CONFIG.appMode === APP_MODE.LOCAL ) {
			this.toaster.warning( `${ warningMessage }\ni18nString:${ i18nStr }` );
		}
	}

	setCloseButton = ( value: boolean ) => this.toasterConfig.closeButton = value;

	setEnableHtml = ( value: boolean ) => this.toasterConfig.enableHtml = value;

	setMessageClass = ( value: string ) => this.toasterConfig.messageClass = value;

	setProgressBar = ( value: boolean ) => this.toasterConfig.progressBar = value;

	setTapToDismiss = ( value: boolean ) => this.toasterConfig.tapToDismiss = value;

	setTimeOut = ( value: number ) => this.toasterConfig.timeOut = value;

	setTitleClass = ( value: string ) => this.toasterConfig.titleClass = value;

	clear() {
		this.toaster.clear();
	}

	error( message: string, title: string = null, override: IToasterConfig = null ) {
		const config = override || this.toasterConfig;
		this.toaster.error( message, title, config );
	}

	info( message: string, title: string = null, override: IToasterConfig = null ) {
		const config = override || this.toasterConfig;
		this.toaster.info( message, title, config );
	}

	success( message: string, title: string = null, override: IToasterConfig = null ) {
		const config = override || this.toasterConfig;
		this.toaster.success( message, title, config );
	}

	show( message: string, title: string = null, override: IToasterConfig = null ) {
		const config = override || this.toasterConfig;
		this.toaster.show( message, title, config );
	}

	warning( message: string, title: string = null, override: IToasterConfig = null ) {
		const config = override || this.toasterConfig;
		this.toaster.warning( message, title, config );
	}

	dispatchByRequestState( requestState: RequestState, entityName: string, action: actionStatus, error: HttpErrorResponse, onlyError?: boolean ) {
		const requestComplete: RequestCompleteBaseState<ENTITY_TYPE> = {
			requestState,
			error: error && error instanceof HttpErrorResponse ? error : null,
			action: null
		};

		this.dispatch( requestComplete, entityName, action, onlyError );
	}

	dispatch(
		state: RequestCompleteBaseState<any> | RequestCompleteEntityState<any> | RequestCompleteListState<any>,
		entityName: string,
		action: actionStatus,
		onlyError?: boolean
	): void {

		if ( state?.requestState === RequestState.FAILED_REQUEST_PRE_VALIDATION && APP_CONFIG.appMode === APP_MODE.LOCAL ) {
			this._loggerService.error(
				this._translationService.getI18nString( false, null, 'error.shared.apiRequestPreValidationFailed' )
			);

			return;
		}

		if ( state?.requestState === RequestState.NOT_SUPPORTED && onlyError !== true ) {
			this._loggerService.error(
				this._translationService.getI18nString( false, null, 'error.shared.apiEndpointNotSupported' )
			);

			return;
		}

		const translatedString = this._translationService.getApiRequestStateMessage(
			state,
			entityName,
			action,
			onlyError,
			true
		);

		if ( !translatedString ) { return; }

		if ( state?.requestState === RequestState.FAILED ) {
			this.toaster.error( translatedString );
		}

		if ( state?.requestState === RequestState.SUCCESS && action !== actionStatus.LIST && onlyError !== true ) {
			this.toaster.success( translatedString );
		}
	}
}

export enum actionStatus {
	ACKNOWLEDGE = 'acknowledge',
	ACTIVATE = 'activate',
	ADD = 'add',
	CHANGE_PASSWORD = 'changePassword',
	CHANGE_STATUS = 'changeStatus',
	COMPLAIN = 'complain',
	CONFIRMATION = 'confirmation',
	DECOMMISSION = 'decommission',
	DELETE = 'delete',
	DISABLE = 'disable',
	DOWNLOAD = 'download',
	EXPORT = 'export',
	EXTEND = 'extend',
	INACTIVE = 'inactive',
	INSTALL = 'install',
	METADATA_SUMMARY = 'metadataSummary',
	LIST = 'list',
	LIST_BY_BPREMISES_ID = 'listByBPremisesId',
	REACTIVATE = 'reactivate',
	READ = 'read',
	REQUEST_NEW_FCC_PASSWORD = 'requestNewFccPassword',
	RENEW = 'renew',
	RESET_PASSWORD = 'resetPassword',
	RETURN = 'return',
	SEARCH = 'search',
	SUBMIT = 'submit',
	SUBMIT_ELSTER_DATA = 'submitElsterData',
	SUSPEND = 'suspend',
	TRIGGER_ERS_SYNC = 'triggerErsSync',
	TRIGGER_UPLOAD = 'triggerUpload',
	UPDATE = 'update'
}
